<template>
    <div>
        <q-card class="no-shadow border-grey" style="min-width: 250px">
            <q-icon
                v-if="isNotified"
                name="fa-light fa-bell-on"
                color="secondary"
                size="xs"
                style="right: 0"
                class="q-mt-md q-mr-md absolute"
            />
            <div class="cursor-pointer" @click="handleCardClick">
                <q-card-section
                    :class="$q.screen.lt.md ? 'q-pa-sm' : 'q-pa-lg'"
                >
                    <div class="row justify-center q-my-md">
                        <q-icon
                            :name="getStatusIcon"
                            :color="isOffline ? 'negative' : 'positive'"
                            size="xl"
                        />
                    </div>
                    <div class="row justify-center q-my-md">
                        <div class="text-h5 text-weight">
                            {{ serialNumber }}
                        </div>
                    </div>

                    <div
                        class="row justify-between q-mb-sm gt-sm text-secondary"
                    >
                        <div class="col">
                            {{ $t('Last 30 Days') }}
                        </div>
                        <div class="col text-right">
                            {{ getFormattedUptime }}
                        </div>
                    </div>
                    <!-- Timeline bars -->
                    <div class="row justify-between timeline-bars gt-sm">
                        <div
                            v-for="day in disconnectionTimeline"
                            :key="day.date"
                            :class="
                                day.hasDisconnection
                                    ? 'hasDisconnection'
                                    : 'noDisconnection'
                            "
                        >
                            <q-tooltip
                                anchor="top middle"
                                self="bottom middle"
                                :offset="[10, 5]"
                            >
                                <div align="center">
                                    {{ getFormattedDate(day.date) }}
                                </div>
                                <div
                                    v-if="day.durationInSeconds"
                                    align="center"
                                >
                                    {{
                                        getFormattedDuration(
                                            day.durationInSeconds
                                        )
                                    }}
                                    <div>
                                        {{ day.count }}
                                        {{ $t('Disconnections') }}
                                    </div>
                                </div>
                                <div v-else>
                                    {{ $t('Operational') }}
                                </div>
                            </q-tooltip>
                        </div>
                    </div>
                </q-card-section>
            </div>
            <q-separator />
            <q-card-actions
                class="row justify-between items-center q-py-md q-px-lg text-secondary"
                :class="$q.screen.lt.md ? 'q-pa-sm' : 'q-py-sm q-px-md'"
                align="center"
                :vertical="$q.screen.lt.md ? true : false"
            >
                <!-- Linked assets button -->
                <q-btn
                    outline
                    color="secondary"
                    :class="$q.screen.lt.md ? 'q-mb-md full-width' : ''"
                    @click="handleLinkedAssetsBtnClick"
                    :disable="assetsCount === 0"
                >
                    {{ assetsCount }} {{ $t('Assets') }}
                </q-btn>

                <!-- Total count and durations of disconnections -->
                <div :class="$q.screen.lt.md ? 'text-center' : 'text-right'">
                    {{ totalDisconnectionsCount }}
                    {{ $t('Disconnections') }}
                    <span class="block">{{
                        getFormattedDuration(totalDurationInSeconds)
                    }}</span>
                </div>
            </q-card-actions>
        </q-card>
    </div>
</template>
<script>
import { date } from 'quasar';
export default {
    name: 'DeviceManagementCard',
    emits: ['cardClick', 'buttonClick'],
    props: {
        id: {
            type: Number,
            required: true
        },
        isOffline: {
            type: Boolean,
            required: true
        },
        serialNumber: {
            type: String,
            required: true
        },
        uptime: {
            type: Number,
            required: true
        },
        totalDurationInSeconds: {
            type: Number,
            required: true
        },
        totalDisconnectionsCount: {
            type: Number,
            required: true
        },
        isNotified: {
            type: Boolean,
            required: false,
            default: false
        },
        disconnectionTimeline: {
            type: Array,
            required: true,
            validator(propValue) {
                // Check if props is an array
                if (!Array.isArray(propValue)) {
                    return false;
                }

                // Check if item is an object
                function validateItemObject(item) {
                    return typeof item === 'object';
                }

                // Check if item has properties
                function validateItemProperties(item) {
                    return (
                        Object.hasOwn(item, 'count') ||
                        Object.hasOwn(item, 'date') ||
                        Object.hasOwn(item, 'durationInSeconds') ||
                        Object.hasOwn(item, 'hasDisconnection')
                    );
                }

                // Check if item properties has the right types
                function validateItemPropertiesTypes(item) {
                    return (
                        typeof item.count === 'number' ||
                        typeof item.date === 'string' ||
                        typeof item.durationInSeconds === 'number' ||
                        typeof item.hasDisconnection === 'boolean'
                    );
                }

                // Check each object inside the array
                for (const el of propValue) {
                    if (!validateItemObject(el)) {
                        return false;
                    }
                    if (!validateItemProperties(el)) {
                        return false;
                    }
                    if (!validateItemPropertiesTypes(el)) {
                        return false;
                    }
                }
                return true;
            }
        },
        assetsCount: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            // loading animation
            isLoading: false,

            linkedAssetsTableColumn: [
                {
                    name: 'name',
                    required: true,
                    label: 'Asset Name',
                    align: 'left',
                    field: row => row.name,
                    sortable: true
                }
            ]
        };
    },
    computed: {
        /**
         * Returns status icon based on isOline prop.
         * @return {String} - font awesome icon class.
         */
        getStatusIcon() {
            if (this.isOffline) {
                return 'fa-solid fa-circle-xmark';
            }
            return 'fa-solid fa-circle-check';
        },
        /**
         * Returns uptime formatted as percentage (3 decimal places)
         * @return {String} - 99.999%
         */
        getFormattedUptime() {
            return this.$t('{uptime}% uptime', {
                uptime: this.uptime.toFixed(3)
            });
        }
    },
    methods: {
        /**
         * Returns total of disconnected time formatted.
         * @return {String} - 99h 59min / 59min 15sec / 15sec
         */
        getFormattedDuration(totalSeconds) {
            if (totalSeconds < 60) {
                return `${Math.round(totalSeconds)} seconds`;
            }
            const totalMinutes = Math.floor(totalSeconds / 60);
            const hours = Math.floor(totalMinutes / 60);
            const minutes = Math.round(totalMinutes % 60);
            const seconds = Math.round(totalSeconds % 60);

            if (hours > 0) {
                return this.$t('{hours}h {minutes}min', {
                    hours: hours,
                    minutes: minutes
                });
            } else {
                return this.$t('{minutes}min {seconds}sec', {
                    minutes: minutes,
                    seconds: seconds
                });
            }
        },
        /**
         * Handle the user click on the card itself
         * to see more details about this specific device
         */
        handleCardClick() {
            this.$emit('cardClick', this.id);
        },
        /**
         * Handle the user click on the assets button
         * to see all assets linked to this device
         */
        handleLinkedAssetsBtnClick() {
            this.$emit('buttonClick', this.id);
        },
        /**
         * Get formatted date (short month and day) from a string dd/mm/yyyy.
         * @return {String}
         */
        getFormattedDate(dateStr) {
            const [day, month, year] = dateStr.split('/');
            // Convert strings to numbers and create a date object
            const timeStamp = new Date(+year, month - 1, +day);
            // Use quasar date to format the return
            return date.formatDate(timeStamp, 'MMM, DD');
        }
    }
};
</script>

<style lang="scss" scoped>
.card-status-mobile {
    min-height: 245px;
    padding-top: 65px;
}
.timeline-bars div {
    width: 6px;
    height: 30px;
    border-radius: 2px;
}

.timeline-bars .hasDisconnection {
    background-color: #d92113;
}
.timeline-bars .noDisconnection {
    background-color: #52a819;
}
#linkedAssetDialog {
    min-width: 40vw;
}

#linkedAssetDialog .top-bar {
    height: 35px;
}
</style>
